import { useMemo, useState } from "react";
import {
    formatCompactEventDate,
    formatCompactEventTime,
    getDeletedEvents,
} from "./calendarEventUtils";
import "./CalendarEventsBulkDelete.css";

function CalendarEventsBulkDeleteNotice({ count, onOpen }) {
    if (count === 0) {
        return null;
    }

    const countLabel = count === 1 ? '1 esdeveniment no trobat' : `${count} esdeveniments no trobats`;
    const buttonLabel = count === 1 ? "Esborra'l" : "Esborra'ls tots";

    return (
        <div className="calendar-events-bulk-notice" role="status">
            <div>
                <strong>{countLabel}</strong>
                <span>Marcats a la taula perquè ja no apareixen a Google Calendar.</span>
            </div>
            <button
                type="button"
                className="calendar-events-bulk-delete-button"
                onClick={onOpen}
            >
                {buttonLabel}
            </button>
        </div>
    );
}

function CalendarEventsDeleteModal({ events, onClose, onConfirm }) {
    const [selectedIds, setSelectedIds] = useState(() => events.map(event => event.id));
    const selectedSet = new Set(selectedIds);
    const allSelected = events.length > 0 && selectedIds.length === events.length;
    const selectedCount = selectedIds.length;

    const toggleEvent = eventId => {
        setSelectedIds(currentIds => (
            currentIds.includes(eventId)
                ? currentIds.filter(id => id !== eventId)
                : [...currentIds, eventId]
        ));
    };

    const toggleAll = () => {
        setSelectedIds(allSelected ? [] : events.map(event => event.id));
    };

    const confirmLabel = selectedCount === 1 ? 'Esborra 1 seleccionat' : `Esborra ${selectedCount} seleccionats`;

    return (
        <div className="calendar-events-delete-modal-overlay" role="presentation" onMouseDown={onClose}>
            <div
                className="calendar-events-delete-modal"
                role="dialog"
                aria-modal="true"
                aria-label="Esborrar esdeveniments no trobats"
                onMouseDown={event => event.stopPropagation()}
            >
                <div className="calendar-events-delete-modal-header">
                    <div>
                        <h3>Esborrar esdeveniments no trobats</h3>
                        <p>Perdràs les proves i assistències associades als esdeveniments seleccionats.</p>
                    </div>
                    <button
                        type="button"
                        className="calendar-events-delete-modal-close"
                        aria-label="Tanca"
                        onClick={onClose}
                    >
                        ×
                    </button>
                </div>

                <label className="calendar-events-delete-modal-select-all">
                    <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                    />
                    <span>Selecciona tots</span>
                </label>

                <div className="calendar-events-delete-modal-list">
                    {events.map(event => {
                        const eventTime = formatCompactEventTime(event);

                        return (
                            <label key={event.id} className="calendar-events-delete-modal-row">
                                <input
                                    type="checkbox"
                                    checked={selectedSet.has(event.id)}
                                    onChange={() => toggleEvent(event.id)}
                                />
                                <span className="calendar-events-delete-modal-date">
                                    {formatCompactEventDate(event)}
                                    {eventTime ? <small>{eventTime}</small> : null}
                                </span>
                                <span className="calendar-events-delete-modal-title">
                                    {event.title || `Esdeveniment #${event.id}`}
                                    {event.lloc ? <small>{event.lloc}</small> : null}
                                </span>
                            </label>
                        );
                    })}
                </div>

                <div className="calendar-events-delete-modal-footer">
                    <button
                        type="button"
                        className="calendar-events-delete-modal-secondary"
                        onClick={onClose}
                    >
                        Cancel·la
                    </button>
                    <button
                        type="button"
                        className="calendar-events-delete-modal-danger"
                        disabled={selectedCount === 0}
                        onClick={() => {
                            onConfirm?.(selectedIds);
                            onClose();
                        }}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

function CalendarEventsBulkDelete({
    events,
    eventsToBeDeleted,
    onDeleteEventsToBeDeleted,
}) {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const deletedEvents = useMemo(
        () => getDeletedEvents(events, eventsToBeDeleted),
        [events, eventsToBeDeleted]
    );

    return (
        <>
            <CalendarEventsBulkDeleteNotice
                count={(eventsToBeDeleted || []).length}
                onOpen={() => setShowDeleteModal(true)}
            />
            {showDeleteModal ? (
                <CalendarEventsDeleteModal
                    events={deletedEvents}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={onDeleteEventsToBeDeleted}
                />
            ) : null}
        </>
    );
}

export default CalendarEventsBulkDelete;
